/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import { queryCache } from 'react-query';
import { buffers, eventChannel } from 'redux-saga';
import { ActionPattern, all, call, fork, put, take } from 'redux-saga/effects';

import { ServerEvent, ServerEventInterfaces, ServerEvents } from 'shared';

import { getCommonEventKey, getOrganizationInvitationEventKey } from '../../constants/notificationsKeys';
import { socketClient } from '../../socket';
import { fetchCluster } from '../../utils/queryFns';
import { reactToCreateLogicalBackup, reactToUpdateCluster } from '../../utils/reactions';
import { authRefreshUser } from '../auth/actions';
import { enqueueHUSnackbarByEvent } from '../notifier/actions';
import { addNotification } from './actions';

const createSocketChannel = () =>
  eventChannel<ServerEvent<ServerEvents>>(emit => {
    const handler = (data: ServerEvent<ServerEvents>) => {
      emit(data);
    };
    socketClient.subscribe(handler);
    return () => {
      socketClient.unsubscribe(handler);
    };
  }, buffers.expanding<ServerEvent<ServerEvents>>());

export function* initSocketSubscription() {
  const socketChannel: ActionPattern = yield call(createSocketChannel);
  while (true) {
    const event: ServerEvent<ServerEvents> = yield take(socketChannel);

    const transformedEvent = {
      id: getCommonEventKey(),
      type: event.type,
      event: event.payload,
      createdAt: new Date(),
    };

    let showSnackbar: boolean = true;
    switch (event.type) {
      case ServerEvents.OrganizationInvitation:
      case ServerEvents.RemovedFromOrganization:
        if (event.type === ServerEvents.OrganizationInvitation) {
          transformedEvent.id = getOrganizationInvitationEventKey(
            (event as ServerEvent<ServerEvents.OrganizationInvitation>).payload.invitationId,
          );
        }
        yield put(addNotification(transformedEvent));
        yield put(authRefreshUser());
        break;
      case ServerEvents.RemovedFromProject:
      case ServerEvents.AddedToProject:
        yield put(addNotification(transformedEvent));
        break;
      case ServerEvents.ImportStarted:
      case ServerEvents.ImportSuccessful:
      case ServerEvents.ImportAborted:
      case ServerEvents.ImportFailedInsufficientSpace:
      case ServerEvents.ImportFailedSchemaExists:
      case ServerEvents.ImportFailedDatabaseNotFound:
      case ServerEvents.ImportFailedInvalidCredentials:
      case ServerEvents.ImportFailed: {
        const { organizationId, projectId, clusterId } =
          event.payload as ServerEventInterfaces[ServerEvents.ImportStarted];
        yield call(() =>
          queryCache.prefetchQuery([fetchCluster.key, { organizationId, projectId, clusterId }], fetchCluster.fn),
        );
        void reactToUpdateCluster({
          organizationId,
          projectId,
          clusterId,
        });
        yield put(addNotification(transformedEvent));
        break;
      }
      case ServerEvents.ClusterCreated:
      case ServerEvents.ClusterUpdated: {
        const { organizationId, projectId, clusterId } =
          event.payload as ServerEventInterfaces[ServerEvents.ClusterCreated];
        yield call(() =>
          queryCache.prefetchQuery([fetchCluster.key, { organizationId, projectId, clusterId }], fetchCluster.fn),
        );
        void reactToUpdateCluster({
          organizationId,
          projectId,
          clusterId,
        });
        yield put(addNotification(transformedEvent));
        break;
      }
      case ServerEvents.LogicalBackupCompleted:
      case ServerEvents.LogicalBackupFailed: {
        const { organizationId, projectId, clusterId } =
          event.payload as ServerEventInterfaces[ServerEvents.LogicalBackupCompleted];
        void reactToCreateLogicalBackup({
          organizationId,
          projectId,
          clusterId,
        });
        yield call(() =>
          queryCache.prefetchQuery([fetchCluster.key, { organizationId, projectId, clusterId }], fetchCluster.fn),
        );
        yield put(addNotification(transformedEvent));
        break;
      }
      default:
        showSnackbar = false;
        break;
    }

    if (showSnackbar) {
      yield put(enqueueHUSnackbarByEvent(transformedEvent));
    }
  }
}

export function* notificationsMainSaga(): Generator {
  return all([yield fork(initSocketSubscription)]);
}
